import { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Autocomplete,
  Card,
  Divider,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import PriceDisplay from "components/PriceDisplay";
import PartnerPriceSection from "layouts/pages/servicelists/PartnerPriceSection";
import webserverService from "services/webserverService";
import Swal from "sweetalert2";
import { AuthContext } from "context";

function WebserverConfig({ onBack }) {
  const { user } = useContext(AuthContext);
  // URL-Parameter auslesen
  const urlParams = new URLSearchParams(window.location.search);
  const urlMode = urlParams.get('mode');
  const urlServiceName = urlParams.get('serviceName');
  
  // Modus: 'new' für neuen Webserver, 'add' für Domains zu bestehendem Webserver hinzufügen
  const [mode, setMode] = useState(urlMode === 'add' ? 'add' : 'new');
  const [existingWebservers, setExistingWebservers] = useState([]);
  const [selectedWebserver, setSelectedWebserver] = useState(null);
  const [storageSize, setStorageSize] = useState("");
  const [domains, setDomains] = useState("");
  const [domainNames, setDomainNames] = useState([]);
  const [mailboxes, setMailboxes] = useState("");
  const [databases, setDatabases] = useState("");
  const [customServiceName, setCustomServiceName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [endCustomerPrice, setEndCustomerPrice] = useState('');

  const showSuccessNotification = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Erfolg!',
      text: message,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showErrorNotification = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: message,
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  };

  const showWarningNotification = (message) => {
    Swal.fire({
      icon: 'warning',
      title: 'Achtung',
      text: message,
      confirmButtonColor: '#f8bb86',
      confirmButtonText: 'OK'
    });
  };

  const storagePrices = {
    "10gb": 2,
    "25gb": 3,
    "50gb": 5,
    "100gb": 8,
    "250gb": 14,
  };

  // Preise für verschiedene Top-Level-Domains
  const domainPrices = {
    "de": 8,
    "com": 18,
    "info": 28,
    "eu": 15,
    "biz": 26,
    "at": 14,
    "net": 19,
    "org": 22,
    // Keine "default" Option mehr, da nur bekannte TLDs erlaubt sind
  };
  
  // Liste der erlaubten TLDs
  const allowedTLDs = Object.keys(domainPrices);
  
  // Preise für Postfächer
  const mailboxPrices = {
    "50": 1.5,
    "100": 5,
    "200": 10
  };
  
  // Preise für Datenbanken
  const databasePrices = {
    "10": 0,
    "30": 2.5,
    "50": 5,
    "100": 8
  };

  // Funktion zum Extrahieren der TLD aus einem Domainnamen
  const getTLD = (domainName) => {
    if (!domainName) return "";
    const parts = domainName.trim().split(".");
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
  };

  // Funktion zum Überprüfen, ob eine TLD erlaubt ist
  const isTLDAllowed = (domainName) => {
    const tld = getTLD(domainName);
    return allowedTLDs.includes(tld);
  };

  // Funktion zum Berechnen des Preises für eine Domain basierend auf ihrer TLD
  const getDomainPrice = (domainName) => {
    const tld = getTLD(domainName);
    return domainPrices[tld] || 0;
  };

  const calculatePrice = () => {
    let basePrice = 0;
    
    if (mode === "new") {
      // Berechnung für neuen Webserver
      basePrice = storagePrices[storageSize] || 0;
      
      // Füge Kosten für Postfächer hinzu
      basePrice += mailboxPrices[mailboxes] || 0;
      
      // Füge Kosten für Datenbanken hinzu
      basePrice += databasePrices[databases] || 0;
    }
    
    // Berechne den Preis für die Domains basierend auf ihren TLDs (für beide Modi)
    // Die erste Domain ist kostenlos, wenn es eine .de-Domain ist, für alle weiteren Domains werden die normalen Preise berechnet
    if (domainNames.length > 0) {
      const domainsCost = domainNames.reduce((total, domain, index) => {
        // Erste Domain (index 0) ist nur kostenlos, wenn es eine .de-Domain ist
        if (index === 0 && domain && getTLD(domain) === "de") {
          return total; // Keine Kosten für die erste Domain, wenn es eine .de-Domain ist
        }
        return total + (domain ? getDomainPrice(domain) : 0);
      }, 0);
      basePrice += domainsCost;
    }
    
    const isPartner = user?.isPartner === true;

    if (isPartner) {
      // 20% Rabatt für Partner
      setOriginalPrice(basePrice);
      setTotalPrice(basePrice * 0.8);
      setDiscountApplied(true);
    } else {
      setTotalPrice(basePrice);
      setOriginalPrice(null);
      setDiscountApplied(false);
    }
  };

  // Lade vorhandene Webserver beim ersten Laden der Komponente
  useEffect(() => {
    const fetchExistingWebservers = async () => {
      try {
        const response = await webserverService.getActiveWebservers();

        if (response.data && Array.isArray(response.data)) {
          // Filtere nur Webserver-Einträge (keine Domains)
          const webservers = response.data.filter(service => 
            service && service.service_type === 'webserver'
          );
          
          // Normalisiere die Daten: Verwende servicename (aus DB) als serviceName für die Frontend-Komponenten
          const normalizedWebservers = webservers.map(webserver => ({
            ...webserver,
            serviceName: webserver.servicename // Wichtig: Hier wird servicename zu serviceName
          }));

          setExistingWebservers(normalizedWebservers);
          
          // Wenn ein serviceName in der URL angegeben wurde, wähle den entsprechenden Webserver aus
          if (urlServiceName && urlMode === 'add') {
            const matchingWebserver = normalizedWebservers.find(
              webserver => webserver.serviceName === urlServiceName || webserver.servicename === urlServiceName
            );
            
            if (matchingWebserver) {
              setSelectedWebserver(matchingWebserver);
            }
          }
        } else {
          setExistingWebservers([]);
        }
      } catch (error) {
        console.error("Fehler beim Laden der vorhandenen Webserver:", error);
        showErrorNotification("Fehler beim Laden der vorhandenen Webserver. Bitte versuchen Sie es später erneut.");
      }
    };

    fetchExistingWebservers();
  }, [urlServiceName, urlMode]);

  useEffect(() => {
    calculatePrice();
  }, [storageSize, domains, domainNames, mailboxes, databases, mode, user?.isPartner]);

  // Aktualisiere die Domainnamen-Felder, wenn sich die Anzahl der Domains ändert
  useEffect(() => {
    if (domains) {
      const count = parseInt(domains);
      // Behalte bestehende Domainnamen bei, füge neue hinzu oder entferne überschüssige
      setDomainNames(prevNames => {
        const newNames = [...prevNames];
        // Anpassen der Array-Länge an die ausgewählte Anzahl von Domains
        if (newNames.length < count) {
          // Füge leere Strings hinzu, wenn mehr Domains ausgewählt wurden
          return [...newNames, ...Array(count - newNames.length).fill("")];
        } else if (newNames.length > count) {
          // Entferne überschüssige Domains, wenn weniger ausgewählt wurden
          return newNames.slice(0, count);
        }
        return newNames;
      });
    } else {
      setDomainNames([]);
    }
  }, [domains]);

  const handleDomainNameChange = (index, value) => {
    const updatedNames = [...domainNames];
    updatedNames[index] = value;
    setDomainNames(updatedNames);
  };

  const handleOrder = async () => {
    // Überprüfe, ob alle Domainnamen eingegeben wurden
    const allDomainsFilled = domainNames.every(domain => domain.trim() !== "");
    
    if (!allDomainsFilled) {
      showWarningNotification("Bitte geben Sie alle Domainnamen ein.");
      return;
    }
    
    // Überprüfe, ob alle TLDs erlaubt sind
    const allTLDsAllowed = domainNames.every(domain => isTLDAllowed(domain));
    
    if (!allTLDsAllowed) {
      showWarningNotification("Bitte verwenden Sie nur die erlaubten Top-Level-Domains: " + allowedTLDs.map(tld => `.${tld}`).join(", "));
      return;
    }
    
    if (agreedToTerms) {
      const userId = user?.id;
      
      try {
        let response;
        
        if (mode === "new") {
          // Bestellung eines neuen Webservers
          // Verwende den benutzerdefinierten Namen, wenn vorhanden, sonst erstelle einen aussagekräftigen Service-Namen
          let serviceName;
          if (customServiceName && customServiceName.trim() !== "") {
            serviceName = customServiceName.trim();
          } else {
            const primaryDomain = domainNames[0] || "";
            serviceName = primaryDomain ? `Webserver für ${primaryDomain}` : `Webserver mit ${domains} Domain(s)`;
          }
          
          const orderDetails = {
            userId,
            storage: storageSize,
            domains: parseInt(domains),
            domainNames: domainNames,
            mailboxes: parseInt(mailboxes) || 0,
            databases: parseInt(databases) || 0,
            serviceName: serviceName,
            totalPrice,
            endCustomerPrice: endCustomerPrice || undefined,
          };

          // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
          if (discountApplied) {
            orderDetails.priceInfo = {
              originalPrice: originalPrice,
              discountApplied: true
            };
          }
          
          response = await webserverService.orderWebserver(orderDetails);
        } else {
          // Hinzufügen von Domains zu einem bestehenden Webserver
          if (!selectedWebserver) {
            showWarningNotification("Bitte wählen Sie einen Webserver aus.");
            return;
          }
          
          // Verwende den richtigen Servicenamen aus dem ausgewählten Webserver
          const serviceName = selectedWebserver.serviceName || selectedWebserver.servicename;
          
          const orderDetails = {
            userId,
            serviceName: serviceName,
            // Wir benötigen immer noch die ID für die Backend-Verarbeitung
            webserverId: selectedWebserver.id,
            domains: parseInt(domains),
            domainNames: domainNames,
            totalPrice,
          };
          
          // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
          if (discountApplied) {
            orderDetails.priceInfo = {
              originalPrice: originalPrice,
              discountApplied: true
            };
          }
          
          response = await webserverService.addDomainsToWebserver(orderDetails);
        }

        if (response.status === 200) {
          const successMessage = mode === "new" 
            ? "Ihre Bestellung wurde erfolgreich aufgegeben!" 
            : "Die Domains wurden erfolgreich zu Ihrem Webserver hinzugefügt!";
            
          showSuccessNotification(successMessage);
          setTimeout(() => {
            window.location.href = "/iaas/server";
          }, 2000);
        } else {
          showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
        }
      } catch (error) {
        console.error("Fehler bei der Bestellung:", error);
        showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
      }
    } else {
      showWarningNotification("Bitte stimmen Sie den AGBs zu, um fortzufahren.");
    }
  };

  const isOrderDisabled = 
    // Gemeinsame Validierung für beide Modi
    !domains || parseInt(domains) < 1 || parseInt(domains) > 15 || !agreedToTerms || 
    (domains && (domainNames.some(domain => domain.trim() === "") || domainNames.some(domain => !isTLDAllowed(domain)))) ||
    
    // Modus-spezifische Validierung
    (mode === "new" ? 
      (!storageSize || !mailboxes || !databases) : 
      // Prüfe, ob ein Webserver ausgewählt wurde
      !selectedWebserver);

  return (
    <SoftBox>
      <SoftBox p={3}>
        <SoftTypography variant="h5" fontWeight="bold">
          Webserver Konfiguration
        </SoftTypography>
        <SoftBox mt={2} mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                Was möchten Sie tun?
              </SoftTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      border: mode === 'new' ? '2px solid #1A73E8' : 'none',
                      boxShadow: mode === 'new' ? '0 4px 20px 0 rgba(0,0,0,0.14)' : '0 2px 8px 0 rgba(0,0,0,0.1)'
                    }}
                    onClick={() => setMode('new')}
                  >
                    <SoftTypography variant="h6" color="info" fontWeight="bold">
                      Neuen Webserver bestellen
                    </SoftTypography>
                    <SoftTypography variant="body2">
                      Bestellen Sie einen komplett neuen Webserver mit Speicherplatz, Domains, Postfächern und Datenbanken.
                    </SoftTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      border: mode === 'add' ? '2px solid #1A73E8' : 'none',
                      boxShadow: mode === 'add' ? '0 4px 20px 0 rgba(0,0,0,0.14)' : '0 2px 8px 0 rgba(0,0,0,0.1)',
                      opacity: existingWebservers.length === 0 ? 0.5 : 1
                    }}
                    onClick={() => existingWebservers.length > 0 && setMode('add')}
                  >
                    <SoftTypography variant="h6" color="info" fontWeight="bold">
                      Domains zu bestehendem Webserver hinzufügen
                    </SoftTypography>
                    <SoftTypography variant="body2">
                      {existingWebservers.length > 0 
                        ? "Fügen Sie zusätzliche Domains zu einem Ihrer bestehenden Webserver hinzu."
                        : "Sie haben noch keine aktiven Webserver. Bitte bestellen Sie zuerst einen neuen Webserver."}
                    </SoftTypography>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </SoftBox>
        
        <Divider />
        
        <SoftBox mt={3}>
          <Grid container spacing={3}>
            {mode === "add" && (
              <Grid item xs={12}>
                <SoftBox mb={2}>
                  <SoftTypography variant="h6" fontWeight="medium">
                    Webserver auswählen
                  </SoftTypography>
                  <Autocomplete
                    options={existingWebservers}
                    getOptionLabel={(option) => {
                      if (!option) return "Webserver auswählen";
                      // Verwende entweder serviceName (normalisiert) oder servicename (direkt aus DB)
                      return option.serviceName || option.servicename || `Webserver ${option.id}`;
                    }}
                    value={selectedWebserver}
                    onChange={(event, newValue) => setSelectedWebserver(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" placeholder="Webserver auswählen" />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <div>
                          <div style={{ fontWeight: 'medium' }}>
                            {option.serviceName || option.servicename || `Webserver ${option.id}`}
                          </div>
                          {option.id && <div style={{ fontSize: '0.8rem', color: '#666' }}>ID: {option.id}</div>}
                        </div>
                      </li>
                    )}
                  />
                </SoftBox>
              </Grid>
            )}
            
            {mode === "new" && (
              <>
                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography variant="h6" fontWeight="medium">
                      Speicherplatz
                    </SoftTypography>
                    <Autocomplete
                      options={[
                        { label: "10 GB", value: "10gb" },
                        { label: "25 GB", value: "25gb" },
                        { label: "50 GB", value: "50gb" },
                        { label: "100 GB", value: "100gb" }, 
                        { label: "250 GB", value: "250gb" },
                      ]}
                      getOptionLabel={(option) => option.label}
                      value={storageSize ? { label: storageSize.toUpperCase().replace('GB', ' GB'), value: storageSize } : null}
                      onChange={(event, newValue) => setStorageSize(newValue ? newValue.value : "")}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Speicherplatz auswählen" />
                      )}
                    />
                    <SoftTypography variant="caption" color="text">
                      Preis: {storagePrices[storageSize] || 0} €
                    </SoftTypography>
                  </SoftBox>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography variant="h6" fontWeight="medium">
                      Anzahl E-Mail-Postfächer
                    </SoftTypography>
                    <Autocomplete
                      options={[
                        { label: "50 Postfächer", value: "50" },
                        { label: "100 Postfächer", value: "100" },
                        { label: "200 Postfächer", value: "200" },
                      ]}
                      getOptionLabel={(option) => option.label}
                      value={mailboxes ? { label: `${mailboxes} Postfach${mailboxes > 1 ? 'er' : ''}`, value: mailboxes } : null}
                      onChange={(event, newValue) => setMailboxes(newValue ? newValue.value : "")}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Anzahl Postfächer auswählen" />
                      )}
                    />
                    <SoftTypography variant="caption" color="text">
                      Preis: {mailboxPrices[mailboxes] || 0} €
                    </SoftTypography>
                  </SoftBox>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography variant="h6" fontWeight="medium">
                      Anzahl Datenbanken
                    </SoftTypography>
                    <Autocomplete
                      options={[
                        { label: "10 Datenbank", value: "10" },
                        { label: "30 Datenbanken", value: "30" },
                        { label: "50 Datenbanken", value: "50" },
                        { label: "100 Datenbanken", value: "100" },
                      ]}
                      getOptionLabel={(option) => option.label}
                      value={databases ? { label: `${databases} Datenbank${databases > 1 ? 'en' : ''}`, value: databases } : null}
                      onChange={(event, newValue) => setDatabases(newValue ? newValue.value : "")}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Anzahl Datenbanken auswählen" />
                      )}
                    />
                    <SoftTypography variant="caption" color="text">
                      Preis: {databasePrices[databases] || 0} €
                    </SoftTypography>
                  </SoftBox>
                </Grid>
                
                <Grid item xs={12}>
                  <SoftBox mb={2}>
                    <SoftTypography variant="h6" fontWeight="medium">
                      Benutzerdefinierter Name (optional)
                    </SoftTypography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Name des Webservers"
                      value={customServiceName}
                      onChange={(e) => setCustomServiceName(e.target.value)}
                      placeholder="z.B. Mein Firmen-Webserver"
                      helperText="Geben Sie einen benutzerdefinierten Namen für Ihren Webserver ein, um ihn später leichter identifizieren zu können."
                    />
                  </SoftBox>
                </Grid>
              </>
            )}
            
            <Grid item xs={12} md={mode === "new" ? 6 : 12}>
              <SoftBox mb={2}>
                <SoftTypography variant="h6" fontWeight="medium">
                  Anzahl Domains {mode === "add" ? "hinzufügen " : ""}(max. 15)
                </SoftTypography>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Anzahl Domains"
                  value={domains}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Nur Zahlen zwischen 1 und 15 zulassen
                    if (value === "" || (parseInt(value) >= 1 && parseInt(value) <= 15)) {
                      setDomains(value);
                    }
                  }}
                  inputProps={{
                    min: 1,
                    max: 15,
                    step: 1
                  }}
                  placeholder="Anzahl eingeben (1-15)"
                />
              </SoftBox>
            </Grid>

            {domains && parseInt(domains) > 0 && (
              <Grid item xs={12}>
                <Card>
                  <SoftBox p={3}>
                    <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                      Domainnamen eingeben
                    </SoftTypography>
                    <SoftTypography variant="body2" color="text" mb={2}>
                      Bitte geben Sie die gewünschten Domainnamen ein (z.B. example.com). 
                      Die Preise variieren je nach Top-Level-Domain:
                    </SoftTypography>
                    <SoftBox mb={2} pl={2}>
                      <SoftTypography variant="body2" component="ul">
                        <li>.de: 8€ pro Domain</li>
                        <li>.com: 18€ pro Domain</li>
                        <li>.info: 28€ pro Domain</li>
                        <li>.eu: 15€ pro Domain</li>
                      </SoftTypography>
                      <SoftTypography variant="body2" color="success" fontWeight="bold" mt={1}>
                        Die erste .de-Domain ist kostenlos! Alle anderen Domains (auch als erste Domain) werden nach den obigen Preisen berechnet.
                      </SoftTypography>
                      <SoftTypography variant="body2" color="error" mt={1}>
                        Hinweis: Es werden nur die oben genannten TLDs unterstützt.
                      </SoftTypography>
                    </SoftBox>
                    <Grid container spacing={2}>
                      {domainNames.map((domain, index) => (
                        <Grid item xs={12} md={6} key={index}>
                          <TextField
                            fullWidth
                            label={`Domain ${index + 1}`}
                            variant="outlined"
                            value={domain}
                            onChange={(e) => handleDomainNameChange(index, e.target.value)}
                            placeholder="z.B. meine-domain.de"
                            margin="normal"
                            required
                            error={domain.trim() === "" || (domain.trim() !== "" && !isTLDAllowed(domain))}
                            helperText={
                              domain.trim() === "" ? "Domainname ist erforderlich" : 
                              !isTLDAllowed(domain) ? `Nicht unterstützte TLD. Erlaubt sind: ${allowedTLDs.map(tld => `.${tld}`).join(", ")}` :
                              (index === 0 && getTLD(domain) === "de") ? "Kostenlos (Erste .de-Domain)" : `Preis: ${getDomainPrice(domain)}€`
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </SoftBox>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <SoftBox mb={2} p={3} bgColor="grey-100" borderRadius="lg">
                <SoftTypography variant="h6" fontWeight="medium">
                  <PriceDisplay 
                    price={totalPrice} 
                    originalPrice={originalPrice} 
                    discountApplied={discountApplied} 
                    title="Gesamtpreis:"
                    suffix={mode === "new" ? "/Monat" : " (einmalig)"}
                  />
                </SoftTypography>
                {user?.isPartner && mode === "new" && (
                  <PartnerPriceSection
                    standardPrice={originalPrice || totalPrice}
                    purchasePrice={totalPrice}
                    endCustomerPrice={endCustomerPrice}
                    onChange={setEndCustomerPrice}
                  />
                )}
                <SoftTypography variant="body2" color="text">
                  {mode === "new" 
                    ? "Inkl. Plesk Control Panel, SSL-Zertifikate und tägliche Backups." 
                    : "Preis für die zusätzlichen Domains"}

                  <p><strong>Die erste .de-Domain ist kostenlos!</strong> Alle anderen Domains werden berechnet. Verfügbare TLDs: .de (7€), .com (18€), .info (28€), .eu (15€), .biz (26€), .at (14€), .net (19€), .org (22€)</p>
                </SoftTypography>
              </SoftBox>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                  />
                }
                label={
                  <SoftTypography variant="body2">
                    Ich akzeptiere die{" "}
                    <a href="https://uds-solutions.de/agb" target="_blank" rel="noopener noreferrer">
                      AGBs
                    </a>{" "}
                    und {mode === "new" ? "bestelle den Webserver" : "buche die zusätzlichen Domains"} kostenpflichtig.
                  </SoftTypography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftButton variant="gradient" color="light" onClick={onBack}>
                  Zurück
                </SoftButton>
                <SoftButton
                  variant="gradient"
                  color="info"
                  onClick={handleOrder}
                  disabled={isOrderDisabled}
                >
                  {mode === "new" ? "Webserver bestellen" : "Domains hinzufügen"}
                </SoftButton>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

WebserverConfig.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default WebserverConfig;